import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isPlaceholder, readPresentation, titlePlaceholder } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionLesereihenfolge: Lesson = {
  id: "lesereihenfolge",
  title: "Lesereihenfolge",
  summary: "In welcher Reihenfolge ein Screenreader die Folie vorliest – und wie man sie ändert.",
  durationMinutes: 9,
  learningGoals: [
    "Die Lesereihenfolge einer Folie prüfen und anpassen",
    "Den Titel an den Anfang der Lesereihenfolge stellen",
    "Verstehen, warum die Anordnung auf der Folie dafür nicht zählt",
  ],
  steps: [
    {
      id: "wie-gelesen-wird",
      title: "Wie eine Folie vorgelesen wird",
      explanation: [
        "Ein Screenreader liest die Objekte einer Folie nicht so, wie sie räumlich " +
          "angeordnet sind. Er liest sie in der Reihenfolge, in der sie in der Datei " +
          "stehen – also in der Reihenfolge, in der sie angelegt wurden.",
        "Das führt zu einem überraschenden Ergebnis: Eine Folie, auf der jemand zuerst " +
          "das Bild eingefügt und danach den Titel getippt hat, wird mit dem Bild " +
          "begonnen. Für sehende Zuhörende ist das unsichtbar, für alle anderen " +
          "verwirrend.",
        "Sichtbar wird die Reihenfolge im Auswahlbereich („Start“ → „Anordnen“ → " +
          "„Auswahlbereich“). Wichtig ist die Leserichtung: Vorgelesen wird von unten " +
          "nach oben. Der unterste Eintrag kommt zuerst, der oberste zuletzt. Die Pfeile " +
          "am Kopf des Bereichs verschieben ein Objekt in dieser Liste.",
        "Als Regel gilt: Der Titel gehört ganz nach unten, dann folgt der Inhalt in der " +
          "Reihenfolge, in der er gelesen werden soll. Dekoratives darf oben stehen – " +
          "es wird ohnehin übersprungen, wenn es als dekorativ gekennzeichnet ist.",
      ],
      keyPoints: [
        "Start → Anordnen → Auswahlbereich zeigt die Reihenfolge.",
        "Vorgelesen wird von unten nach oben – der unterste Eintrag zuerst.",
        "Der Titel gehört an das untere Ende der Liste.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass auf jeder Folie der Titelplatzhalter als Erstes gelesen " +
          "wird – er muss im Auswahlbereich ganz unten stehen.",
        hint: "Auswahlbereich öffnen, den Titel markieren und mit dem Abwärtspfeil ans Ende der Liste schieben.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Die Formen einer Folie",
              "1.3",
              "Öffne den Auswahlbereich und sieh nach, ob der Titel dort ganz unten steht.",
            );
          }

          // Office.js liefert die Formen in der Reihenfolge, in der sie in der
          // Datei stehen – das ist genau die Vorlesereihenfolge. Der Titel muss
          // also das erste Element der Sammlung sein.
          const auffaellig = snapshot.slides.filter((folie) => {
            const titel = titlePlaceholder(folie);
            if (!titel) return false;
            const erste = folie.shapes[0];
            return erste !== undefined && erste.id !== titel.id;
          });
          const ohneTitel = snapshot.slides.filter(
            (folie) => folie.shapes.some(isPlaceholder) && titlePlaceholder(folie) === null,
          );

          if (auffaellig.length === 0 && ohneTitel.length === 0) {
            return pass(
              "Auf allen Folien steht der Titel an erster Stelle der Lesereihenfolge – ein Screenreader beginnt damit.",
            );
          }
          if (auffaellig.length === 0) {
            return fail(
              `Auf ${ohneTitel.length === 1 ? "einer Folie" : `${ohneTitel.length} Folien`} gibt es keinen Titelplatzhalter, ` +
                `zum Beispiel auf Folie ${ohneTitel[0].index + 1}.`,
              "Weise der Folie über „Start“ → „Layout“ ein Layout mit Titel zu. Soll der Titel unsichtbar bleiben, blende ihn im Auswahlbereich über das Augensymbol aus.",
            );
          }

          return fail(
            `Auf Folie ${auffaellig[0].index + 1} wird nicht der Titel zuerst gelesen, sondern „${auffaellig[0].shapes[0]?.name ?? "ein anderes Objekt"}“.` +
              (auffaellig.length > 1 ? ` Insgesamt betrifft das ${auffaellig.length} Folien.` : ""),
            "Auswahlbereich öffnen, den Titel markieren und mit dem Abwärtspfeil ans untere Ende der Liste schieben.",
          );
        },
      },
    },
    {
      id: "was-nicht-hilft",
      title: "Was dabei nicht hilft",
      explanation: [
        "Zwei Wege, die naheliegen, ändern die Lesereihenfolge nicht. Der erste ist das " +
          "Verschieben auf der Folie: Ein Objekt weiter oben zu platzieren macht es nicht " +
          "früher gelesen. Der zweite ist „In den Vordergrund“ und „In den Hintergrund“ – " +
          "das ändert zwar die Stapelreihenfolge und damit tatsächlich auch die Position " +
          "im Auswahlbereich, ist aber eine Gestaltungsentscheidung, die man nicht " +
          "nebenbei für die Barrierefreiheit umwidmen sollte.",
        "Der verlässliche Weg bleibt der Auswahlbereich. Er zeigt beides an derselben " +
          "Stelle: die Reihenfolge und, über das Augensymbol, die Sichtbarkeit.",
        "Ein Sonderfall sind Gruppen. Eine Gruppe wird als ein Objekt gelesen, ihre " +
          "Bestandteile in der Reihenfolge innerhalb der Gruppe. Das ist meistens " +
          "erwünscht – eine Grafik mit Beschriftung soll zusammen vorgelesen werden. Der " +
          "Alternativtext der Gruppe ersetzt dann die Einzelbeschreibungen.",
        "Für die tägliche Arbeit gibt es eine einfache Gewohnheit, die das Problem " +
          "weitgehend vermeidet: Erst den Titel tippen, dann den Inhalt, dann die Bilder " +
          "einfügen. Wer in Lesereihenfolge arbeitet, muss sie hinterher selten " +
          "korrigieren.",
      ],
      keyPoints: [
        "Verschieben auf der Folie ändert die Lesereihenfolge nicht.",
        "Gruppen werden als ein Objekt gelesen – Alternativtext an der Gruppe setzen.",
        "Gewohnheit: erst Titel, dann Inhalt, dann Bilder.",
      ],
    },
  ],
};
